import {
  createSelector,
  createSlice,
  type DeepPartial,
  type PayloadAction,
} from '@reduxjs/toolkit'
import _, { isArray, isEmpty } from 'lodash'
import type { RootState } from '@/redux/store'
import {
  createDefaultSearchState,
  defaultSearchResultOptions,
} from './defaults'
import {
  addCriteriaStringToSearchParams,
  encodeCriteriaString,
} from './maps/criteriaString'
import { sanitizeSearchState } from './maps/sanitizeSearchState'
import { mapSearchMetaOptionsToQuery } from './maps/searchMetaOptions'
import { mapStateToQuery } from './maps/searchState'
import type {
  SavedSearch,
  SearchFilterState,
  SearchMetaOptions,
  SearchQueryPayload,
  SearchResultItem,
  SearchType,
} from './types'
import { validateSearch } from './validation'

/**
 * We're maintaining two states of the search parameters here.
 * This is useful to prevent unneeded queries and to automate some settings
 * like resetting page skip/offset on a new search.
 *
 * Active Search Params are related to current results being shown if any.
 * Next Search Params reflect the current state of filters and UI settings.
 */

type SearchState = {
  searchType: SearchType
  nextSearchOptions: SearchFilterState
  activeSearchOptions: SearchFilterState | null
  activeSearchQueryParams: SearchQueryPayload | null
  currentSavedSearch: SavedSearch | null
  searchMetaOptions: SearchMetaOptions
  selectedMedia: SearchResultItem[]
  hasExported: boolean
}

const initialState: SearchState = {
  searchType: 'standard',
  nextSearchOptions: createDefaultSearchState(),
  activeSearchOptions: null,
  activeSearchQueryParams: null,
  currentSavedSearch: null,
  searchMetaOptions: defaultSearchResultOptions,
  selectedMedia: [],
  hasExported: false,
}

const customizeMerge = (prevValue: unknown, nextValue: unknown) => {
  //Overwrite arrays rather than concat
  if (isArray(prevValue)) {
    return nextValue
  }
  if (isEmpty(nextValue)) {
    return nextValue
  }
}

export const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setSearchType: (
      state,
      action: PayloadAction<SearchState['searchType']>,
    ) => {
      state.searchType = action.payload
    },
    //Search Filters
    applyNextSearch: (state, _action: PayloadAction) => {
      state.searchType = 'standard'
      state.searchMetaOptions.skip = 0

      try {
        validateSearch(state.nextSearchOptions)
      } catch (error) {
        console.error(error)
        return
      }

      state.activeSearchOptions = state.nextSearchOptions
      state.hasExported = false

      addCriteriaStringToSearchParams(state.activeSearchOptions)
    },

    setNextSearchOptions: (
      state,
      action: PayloadAction<SearchFilterState | null>,
    ) => {
      const merged = _.mergeWith(
        {},
        createDefaultSearchState(),
        action.payload ?? {},
        customizeMerge,
      )
      state.nextSearchOptions = sanitizeSearchState(merged)
    },

    updateNextSearchOptions: (
      state,
      action: PayloadAction<DeepPartial<SearchFilterState>>,
    ) => {
      const prevSearchOptions = { ...state.nextSearchOptions }
      const nextSearch = _.mergeWith(
        {},
        prevSearchOptions,
        action.payload,
        customizeMerge,
      )

      state.nextSearchOptions = sanitizeSearchState(nextSearch)
    },

    updateActiveSearchOptions: (
      state,
      action: PayloadAction<DeepPartial<SearchFilterState>>,
    ) => {
      if (!state.activeSearchOptions) {
        state.activeSearchOptions = state.nextSearchOptions
      }
      state.activeSearchOptions = _.mergeWith(
        {},
        { ...state.activeSearchOptions },
        action.payload,
        customizeMerge,
      )

      state.activeSearchOptions = sanitizeSearchState(state.activeSearchOptions)
    },

    updateSearchMetaOptions: (
      state,
      action: PayloadAction<DeepPartial<SearchMetaOptions>>,
    ) => {
      const prevSearchMetaOptions = state.searchMetaOptions
      state.searchMetaOptions = _.mergeWith(
        {},
        prevSearchMetaOptions,
        action.payload,
        customizeMerge,
      )
    },

    updateSelection: (
      state,
      action: PayloadAction<SearchResultItem | SearchResultItem[]>,
    ) => {
      let selectMedia = action.payload

      if (Array.isArray(selectMedia) === false) {
        selectMedia = [selectMedia]
      }

      for (const media of selectMedia) {
        if (state.selectedMedia.find((item) => item.id === media.id)) {
          state.selectedMedia = state.selectedMedia.filter(
            (i) => i.id !== media.id,
          )
        } else {
          state.selectedMedia.push(media)
        }
      }
    },

    clearSelection: (state) => {
      state.selectedMedia = []
    },

    resetAll: (state) => {
      state.nextSearchOptions = createDefaultSearchState()
    },

    setHasExported: (state, action: PayloadAction<boolean>) => {
      state.hasExported = action.payload
    },

    // Saved Search
    setCurrentSavedSearch: (
      state,
      action: PayloadAction<SavedSearch | null>,
    ) => {
      state.currentSavedSearch = action.payload
    },
  },
})

export const {
  applyNextSearch,
  setNextSearchOptions,
  updateNextSearchOptions,
  updateActiveSearchOptions,
  updateSearchMetaOptions,
  setCurrentSavedSearch,
  updateSelection,
  clearSelection,
  setSearchType,
  setHasExported,
} = searchSlice.actions

// Memoized root selector
const getSearchState = (state: RootState) => state.reducer.search

export const selectActiveSearchCriteriaString = createSelector(
  [getSearchState],
  (state) =>
    state.activeSearchOptions
      ? encodeCriteriaString(state.activeSearchOptions)
      : null,
)

export const selectSearchType = createSelector(
  [getSearchState],
  (state) => state.searchType,
)

export const selectSearchPayload = createSelector([getSearchState], (state) => {
  const { activeSearchOptions, searchMetaOptions } = state
  if (!activeSearchOptions) return null
  // Avoid creating new object if values are unchanged
  return {
    ...activeSearchOptions,
    options: searchMetaOptions,
  }
})

export const selectActiveSearchParameters = createSelector(
  [getSearchState],
  (state) => state.activeSearchOptions,
)

export const selectNextSearchParameters = createSelector(
  [getSearchState],
  (state) => state.nextSearchOptions,
)

export const selectActiveSearchQueryParams = createSelector(
  [getSearchState],
  (state) => state.activeSearchQueryParams,
)

export const selectSearchMetaOptions = createSelector(
  [getSearchState],
  (state) => state.searchMetaOptions,
)

export const selectNext = {
  mediaTypes: createSelector(
    [getSearchState],
    (state) => state.nextSearchOptions.mediaType,
  ),
  status: createSelector(
    [getSearchState],
    (state) => state.nextSearchOptions.status,
  ),
}

export const selectBypassedFieldRequirements = createSelector(
  [getSearchState],
  (state) => {
    const nextSearch = state.nextSearchOptions
    return {
      subIndustries: Object.values(nextSearch.industries).every(
        (value) => value === false,
      ),
      mediaType: Object.values(nextSearch.mediaType).every(
        (value) => value === false,
      ),
    }
  },
)

export const selectCurrentSavedSearch = createSelector(
  [getSearchState],
  (state) => state.currentSavedSearch,
)
export const selectCurrentSelectionRows = createSelector(
  [getSearchState],
  (state) => state.selectedMedia,
)
export const selectCurrentSelectionRowIds = createSelector(
  [getSearchState],
  (state) => state.selectedMedia.map((row) => row.id),
)

export const selectIsAllSameMediaType = createSelector(
  [getSearchState],
  (state) => {
    const { selectedMedia } = state
    if (selectedMedia.length === 0) return false
    const firstType = selectedMedia[0].mediaType
    return selectedMedia.every((row) => row.mediaType === firstType)
  },
)

export const selectBulkEditQueryString = createSelector(
  [getSearchState],
  (state) => {
    const { selectedMedia } = state
    if (selectedMedia.length === 0) return
    const firstType = selectedMedia[0].mediaType
    if (!selectedMedia.every((item) => item.mediaType === firstType)) return
    const mediaNumbers = selectedMedia.map((item) => item.mediaNumber).join(',')
    const mediaType = firstType
    const SearchParams = new URLSearchParams({ mediaNumbers, mediaType })
    return `${window.location.origin}/n/bulk-edit?${SearchParams}`
  },
)

export const selectHasExported = createSelector(
  [getSearchState],
  (state) => state.hasExported,
)

export const selectSearchQueryForExport = createSelector(
  [getSearchState],
  (state) => {
    const { activeSearchOptions, searchMetaOptions } = state
    if (!activeSearchOptions || !searchMetaOptions) return null
    const adjustedOptions = { ...searchMetaOptions }
    const query = mapStateToQuery(activeSearchOptions)
    const options = mapSearchMetaOptionsToQuery(adjustedOptions)
    const criteriaString = encodeCriteriaString(activeSearchOptions)
    return { ...query, criteriaString, options }
  },
)

export const searchSelectors = {
  selectActiveSearchCriteriaString,
  selectSearchType,
  selectSearchPayload,
  selectActiveSearchParameters,
  selectNextSearchParameters,
  selectActiveSearchQueryParams,
  selectSearchMetaOptions,
  selectNext,
  selectBypassedFieldRequirements,
  selectCurrentSavedSearch,
  selectCurrentSelectionRows,
  selectCurrentSelectionRowIds,
  selectIsAllSameMediaType,
  selectBulkEditQueryString,
  selectHasExported,
  selectSearchQueryForExport,
}

export default searchSlice.reducer
